import { useCallback, useEffect, useRef, useState } from 'react';

import { FILTER_OPERATORS, type FilterOperator, type FilterRule } from './filtering';

/** Board filter selections, keyed by column id (empty array = no narrowing). */
export type BoardFilters = Record<string, readonly string[]>;

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Malformed JSON or storage unavailable (private mode / quota) — ignore.
    return null;
  }
}

function writeJson(key: string, value: unknown, hasContent: boolean): void {
  try {
    if (hasContent) {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      // Nothing to remember — drop the entry rather than storing an empty one.
      localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — persistence is best-effort, never fatal.
  }
}

/**
 * Board state that survives reloads but stays PRIVATE to this browser: it lives
 * in localStorage (never sent to the server), keyed by tracker id, so each board
 * remembers its own and no teammate is affected. Contrast with the tracker's
 * `layout` (columns, views, dashboard) which IS shared workspace-wide.
 *
 * The setter takes a concrete value (not an updater), so the localStorage write
 * stays OUT of a state-updater function — keeping the updater pure per the
 * project's React rules.
 *
 * `keyFor`, `parse` and `hasContent` must be module-scope functions; the setter
 * is only as stable as they are.
 */
function usePersistentTrackerState<T>(
  keyFor: (trackerId: string) => string,
  trackerId: string,
  parse: (raw: unknown) => T,
  hasContent: (value: T) => boolean,
) {
  const [value, setValueState] = useState<T>(() => parse(readJson(keyFor(trackerId))));
  const currentId = useRef(trackerId);

  // Reload when navigating to a different tracker without a remount.
  useEffect(() => {
    if (currentId.current !== trackerId) {
      currentId.current = trackerId;
      setValueState(parse(readJson(keyFor(trackerId))));
    }
  }, [trackerId, keyFor, parse]);

  const setValue = useCallback(
    (next: T) => {
      writeJson(keyFor(currentId.current), next, hasContent(next));
      setValueState(next);
    },
    [keyFor, hasContent],
  );

  return [value, setValue] as const;
}

const filtersKeyFor = (trackerId: string) => `trackers:filters:${trackerId}`;

function parseFilters(raw: unknown): BoardFilters {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  // Unknown column keys are harmless — the board only applies filters for
  // columns that still exist — but the VALUES are handed to `new Set()`, which
  // throws on anything that is not iterable.
  return Object.fromEntries(
    Object.entries(raw).flatMap(([columnId, values]) =>
      Array.isArray(values)
        ? [[columnId, values.filter((v): v is string => typeof v === 'string')]]
        : [],
    ),
  );
}

const hasFilters = (filters: BoardFilters) => Object.keys(filters).length > 0;

export function usePersistentFilters(trackerId: string) {
  return usePersistentTrackerState(filtersKeyFor, trackerId, parseFilters, hasFilters);
}

const rulesKeyFor = (trackerId: string) => `trackers:filter-rules:${trackerId}`;

const isOperator = (value: unknown): value is FilterOperator =>
  FILTER_OPERATORS.includes(value as FilterOperator);

function parseRules(raw: unknown): readonly FilterRule[] {
  if (!Array.isArray(raw)) return [];
  // Rules drive the board, so unlike the loose filter map they are validated
  // field by field: a half-parsed rule would silently hide rows.
  return raw.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== 'object') return [];
    const { id, columnId, operator, values } = entry as Record<string, unknown>;
    if (typeof id !== 'string' || typeof columnId !== 'string' || !isOperator(operator)) return [];
    if (!Array.isArray(values)) return [];
    return [
      { id, columnId, operator, values: values.filter((v): v is string => typeof v === 'string') },
    ];
  });
}

const hasRules = (rules: readonly FilterRule[]) => rules.length > 0;

/** The advanced builder's rule list, stored exactly like the quick selections. */
export function usePersistentFilterRules(trackerId: string) {
  return usePersistentTrackerState(rulesKeyFor, trackerId, parseRules, hasRules);
}

const recentKeyFor = (trackerId: string) => `trackers:recent-filters:${trackerId}`;

/** How many columns the panel remembers; it only ever DISPLAYS the first two. */
const RECENT_CAP = 3;

function parseRecent(raw: unknown): readonly string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string').slice(0, RECENT_CAP);
}

const hasRecent = (recent: readonly string[]) => recent.length > 0;

/**
 * The columns this browser last filtered on, most recent first, so the panel can
 * float them above the full column list.
 *
 * A column that no longer exists is left in storage and skipped by the panel,
 * which reads through the live column list anyway.
 */
export function useRecentFilterColumns(trackerId: string) {
  const [recent, setRecent] = usePersistentTrackerState(
    recentKeyFor,
    trackerId,
    parseRecent,
    hasRecent,
  );

  const noteColumnUsed = useCallback(
    (columnId: string) => {
      if (recent[0] === columnId) return;
      setRecent([columnId, ...recent.filter((id) => id !== columnId)].slice(0, RECENT_CAP));
    },
    [recent, setRecent],
  );

  return [recent, noteColumnUsed] as const;
}
